import { useCallback } from 'react';

import type { StripeError } from '@stripe/stripe-js';

import type { WithTokenParams } from '#src/app/shared/types';
import type { PaymentMethodSaveOptions, PaymentType, StripeValidationErrors } from '#src/app/shared/utils/payments';
import { createStripePaymentMethod, DEFAULT_STRIPE_ERROR_VALIDATION } from '#src/app/shared/utils/payments';

import { useSavePaymentMethod } from './hooks/useSavePaymentMethod';
import { useStripeInstances } from './hooks/useStripeInstances';
import { validateFormBeforeTokenization } from './utils/validateFormBeforeTokenization';

export type Params = {
	email: string;
	name: string;
	paymentMethodSaveOptions: PaymentMethodSaveOptions;
	countryCode: string;
	currency: string;
	beforeToken: (paymentType: PaymentType) => Promise<boolean>;
	validate: (paymentType: PaymentType) => Promise<boolean>;
	withToken: (params: WithTokenParams) => Promise<void>;
	prePaymentChecks?: (paymentType: PaymentType) => Promise<boolean>;
	onStripeError: (error: StripeError) => void;
	handleSubmitFailure: (error: unknown) => void;
	resetErrors: () => void;
	setValidationErrors: React.Dispatch<React.SetStateAction<StripeValidationErrors>>;
};

type Result = {
	handleSubmit: (event: React.FormEvent<HTMLFormElement>) => Promise<void>;
};

export function useStripeFormSubmit(props: Params): Result {
	const { getStripeInstances } = useStripeInstances();
	const { savePaymentMethod } = useSavePaymentMethod();

	const handleSubmit = useCallback(
		async (event: React.FormEvent<HTMLFormElement>) => {
			event.preventDefault();

			if (!(await validateFormBeforeTokenization(props))) return;

			try {
				const { stripe, elements } = getStripeInstances();

				props.setValidationErrors(DEFAULT_STRIPE_ERROR_VALIDATION);

				const createResult = await createStripePaymentMethod(stripe, elements, props.name, props.email);
				if (createResult.error) {
					props.onStripeError(createResult.error);
					return;
				}

				const result = await savePaymentMethod({
					stripe,
					token: createResult.paymentMethod.id,
					email: props.email,
					countryCode: props.countryCode,
					currency: props.currency,
					paymentMethodSaveOptions: props.paymentMethodSaveOptions,
				});
				if (!result.success) {
					props.onStripeError(result.error);
					return;
				}

				const cardType = createResult.paymentMethod.card?.brand ?? undefined;

				void props.withToken({ paymentType: 'creditCard', token: result.token, cardType });
			} catch (error) {
				props.handleSubmitFailure(error);
			}
		},
		[props, getStripeInstances, savePaymentMethod],
	);

	return {
		handleSubmit,
	};
}
